"use client";

import type { Tier, TierId } from "@mw/shared/pricing";
import { getPricingTiers } from "@mw/shared/pricing";
import React, { useMemo, useState, useRef, useEffect } from "react";
import { Tabs, Button, Select as RACSelect, SelectValue } from "react-aria-components";
import type { Key } from "react-aria-components";
import { TabList } from "../core/TabList";
import { ListBox } from "../core/ListBox";
import IconChevronGrabberVertical from "@ui/central-icons/icons/IconChevronGrabberVertical";
import { clsx } from "clsx";
import { LayoutGroup } from "motion/react";
import * as m from "motion/react-m";
import Link from "next/link";
import { TIER_COVER_IMAGES } from "./utils";
import Image from "next/image";
import IconChevronLeft from "@ui/central-icons/icons/IconChevronLeft";
import IconChevronRight from "@ui/central-icons/icons/IconChevronRight";

import type { ModelSection, Section } from "./pricing-sections";
import { DEFAULT_MODELS, DEFAULT_PRODUCTS } from "./pricing-sections";
import { usePricingTableContext } from "./PricingTableContext";
import { setStarterTierPromotion } from "./utils";

// Import data-driven pricing layer
import { getPlatformPricingSections, type PlatformPricingSection } from "./platform-pricing-data";
import type { PlatformPricingTranslations } from "./PlatformPricingTable";
import { Row } from "./offers/shared";
import { DottedBorder } from "../core/GradientBorder";
import { PricingValueRenderer } from "./PricingValueRenderer";

// Import shared utilities
import { ProductionsSectionContent } from "./PricingTableV3Shared";
import { PricingComparisonTable, type PricingTableColumn } from "./PricingComparisonTable";

interface PricingTableTabsProps {
  className?: string;
  models?: ModelSection[];
  products?: Section[];
  offersId?: ((tier: Tier) => boolean) | string[];
  showStarterPromo?: boolean;
  /** Pre-translated labels from server component. If not provided, labels will be empty. */
  translations?: PlatformPricingTranslations;
}

const ProductionsSection = () => {
  return (
    <div>
      <ProductionsSectionContent
        gridClassName='tw-grid md:tw-grid-cols-[minmax(12rem,1fr)_repeat(2,minmax(8rem,1fr))]'
        showFeatureColumn={true}
      />
    </div>
  );
};

const SectionContent = ({
  section,
  tiers,
  translations,
  mobileTier,
  onMobileTierChange,
  allTiers,
}: {
  section: Section;
  tiers: Tier[];
  translations?: PlatformPricingTranslations;
  mobileTier?: Tier;
  onMobileTierChange?: (tier: Tier) => void;
  allTiers?: Tier[];
}) => {
  const { billingCycle } = usePricingTableContext();
  const billingPeriod = billingCycle;

  // Look up section config from data file
  const sectionConfig = getPlatformPricingSections()[section as PlatformPricingSection];

  // Build columns from tiers
  const columns: PricingTableColumn<TierId>[] = tiers.map(tier => {
    const coverImage = TIER_COVER_IMAGES[tier.planId];
    const tierTitle = translations?.tierTitles[tier.planId] ?? tier.planId;
    return {
      id: tier.planId,
      mobileLabel: tierTitle,
      header: (
        <Link
          href={tier.cta.href}
          className='tw-group/header tw-relative tw-flex tw-items-center tw-justify-end tw-h-full tw-pt-10 tw-pb-6 tw-px-4 tw-overflow-hidden tw-no-underline'
        >
          {/* Dark background on hover */}
          <div className='tw-absolute tw-inset-0 tw-bg-black tw-opacity-0 group-hover/header:tw-opacity-100 tw-transition-opacity tw-duration-200 tw-z-10' />

          {/* Cover image overlay on hover */}
          {coverImage && (
            <div className='tw-absolute tw-inset-0 tw-opacity-0 group-hover/header:tw-opacity-100 tw-transition-opacity tw-duration-200 tw-overflow-hidden tw-z-10'>
              <Image src={coverImage} alt='' fill className='tw-object-cover' quality={90} />
            </div>
          )}

          <span className='tw-relative tw-z-20 tw-text-black group-hover/header:tw-text-white tw-type-base tw-transition-colors tw-duration-200'>
            {tierTitle}
          </span>
        </Link>
      ),
    };
  });

  // Build sections from data file subsections
  const sections = sectionConfig
    ? sectionConfig.subSections.map((subSection, subIdx) => ({
        label: subSection.labelKey && translations ? translations.subsections[subSection.labelKey] : undefined,
        rows: subSection.rows.map((row, rowIdx) => {
          const isLastRow = rowIdx === subSection.rows.length - 1;
          return {
            labelCell: (
              <>
                <Row
                  key={`label-${rowIdx}`}
                  styleVariant='v3'
                  title={translations ? translations.rows[row.labelKey] : row.labelKey}
                  tooltip={row.tooltipKey && translations ? translations.tooltips[row.tooltipKey] : undefined}
                  size={row.size}
                  withPadding
                />
                {!isLastRow && <DottedBorder side='inline' />}
              </>
            ),
            valueCells: tiers.map(tier => {
              const { value, subtitle } = row.getValue(tier, billingPeriod);
              const isUnavailable = value.type === "unavailable";
              const isBooleanFalse = value.type === "boolean" && !value.value;
              const showValue = !isUnavailable && !isBooleanFalse;
              return (
                <React.Fragment key={`${tier.planId}-${subIdx}-${rowIdx}`}>
                  <Row
                    styleVariant='v3'
                    align='end'
                    size={row.size}
                    text={
                      showValue ? (
                        subtitle ? (
                          <span className='tw-flex tw-flex-col tw-gap-1 tw-items-end'>
                            <PricingValueRenderer value={value} />
                            <span className='tw-text-gray-500 tw-type-sm'>
                              <PricingValueRenderer value={subtitle} />
                            </span>
                          </span>
                        ) : (
                          <PricingValueRenderer value={value} />
                        )
                      ) : undefined
                    }
                    feature={!showValue ? false : undefined}
                    withPadding
                  />
                  {!isLastRow && <DottedBorder side='inline' />}
                </React.Fragment>
              );
            }),
          };
        }),
      }))
    : [];

  return (
    <PricingComparisonTable<TierId>
      columns={columns}
      sections={sections}
      mobileSelectedColumnId={mobileTier?.planId}
      onMobileColumnChange={planId => {
        const tier = allTiers?.find(t => t.planId === planId);
        if (tier && onMobileTierChange) {
          onMobileTierChange(tier);
        }
      }}
      renderMobileSelector={
        allTiers && onMobileTierChange
          ? ({ columns, selectedColumnId, onSelectionChange }) => (
              <div className='tw-p-4'>
                <RACSelect
                  selectedKey={selectedColumnId}
                  onSelectionChange={(key: Key | null) => {
                    if (key !== null && typeof key === "string") {
                      // Key comes from our column IDs which are TierIds, so this is safe
                      onSelectionChange(key as TierId);
                    }
                  }}
                  aria-label='Select tier'
                  className='tw-w-full'
                >
                  <Button
                    className={({ isFocusVisible }) =>
                      clsx(
                        "tw-w-full tw-text-black tw-type-base tw-flex tw-items-center tw-justify-between tw-cursor-pointer",
                        isFocusVisible ? "tw-outline-focus" : "tw-outline-none",
                      )
                    }
                  >
                    <SelectValue className='tw-flex-1 tw-text-left' />
                    <IconChevronGrabberVertical className='tw-size-5 tw-text-black tw-flex-none' />
                  </Button>
                  <ListBox
                    items={columns.map(col => ({
                      id: col.id,
                      textValue: col.mobileLabel,
                    }))}
                    offset={8}
                  />
                </RACSelect>
              </div>
            )
          : undefined
      }
    />
  );
};

// Helper hook to build tab items from sections
const useSectionTabItems = (
  models: ModelSection[],
  products: Section[],
  translations?: PlatformPricingTranslations,
) => {
  return useMemo(() => {
    const allSections = [
      ...models,
      ...products.flatMap(p => (p === "dubbing" ? ["automatic_dubbing" as const, "dubbing_studio" as const] : [p])),
    ];

    return allSections.map(section => ({
      id: section,
      title: translations?.sectionTitles[section] ?? section,
    }));
  }, [models, products, translations]);
};

const ScrollButton = ({
  direction,
  onClick,
  visible,
  ariaLabel,
}: {
  direction: "left" | "right";
  onClick: () => void;
  visible: boolean;
  ariaLabel: string;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={!visible}
      className={clsx(
        "tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-full tw-outline-none focus-visible:tw-outline-focus tw-transition-all tw-flex-shrink-0",
        visible ? "tw-cursor-pointer tw-opacity-100" : "tw-cursor-default tw-opacity-30",
      )}
      style={{
        backgroundColor: visible && isHovered ? "#eeeceb" : "transparent",
      }}
      aria-label={ariaLabel}
      aria-disabled={!visible}
    >
      {direction === "left" ? (
        <IconChevronLeft className='tw-w-4 tw-h-4 tw-text-black' />
      ) : (
        <IconChevronRight className='tw-w-4 tw-h-4 tw-text-black' />
      )}
    </button>
  );
};

const PricingTableContent = ({
  models,
  products,
  offersId,
  translations,
}: {
  models: ModelSection[];
  products: Section[];
  offersId: ((tier: Tier) => boolean) | string[];
  translations?: PlatformPricingTranslations;
}) => {
  const { selectedSection, setSelectedSection } = usePricingTableContext();

  const filterFn = typeof offersId === "function" ? offersId : (tier: Tier) => offersId.includes(tier.planId);
  // Filter out enterprise tier
  const allTiers = getPricingTiers().filter(tier => filterFn(tier) && tier.planId !== "enterprise");

  // Mobile tier selection state
  const [selectedMobileTier, setSelectedMobileTier] = useState<Tier>(allTiers[0]);

  // Hide conversational_ai tab (it has its own dedicated table)
  const visibleProducts = products.filter(p => p !== "conversational_ai");

  // Get tab items for TabList
  const tabItems = useSectionTabItems(models, visibleProducts, translations);

  // Scroll controls
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollButtons = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 1);
  };

  useEffect(() => {
    updateScrollButtons();
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);

    return () => {
      container.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, []);

  const scrollLeft = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" });
  };

  const scrollRight = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" });
  };

  return (
    <div className='tw-flex tw-flex-col tw-overflow-visible'>
      {/* Section Tabs - Both Desktop and Mobile */}
      <div className='tw-relative tw-border-b tw-py-5 tw-border-black/5'>
        <div className='tw-flex tw-items-center'>
          <div className='tw-flex-shrink-0 tw-pl-2'>
            <ScrollButton
              direction='left'
              onClick={scrollLeft}
              visible={canScrollLeft}
              ariaLabel={translations?.ui.scrollLeft ?? "Scroll left"}
            />
          </div>
          <div className='tw-flex-1 tw-min-w-0 tw-relative'>
            <Tabs selectedKey={selectedSection} onSelectionChange={key => setSelectedSection(key as Section)}>
              <TabList
                items={tabItems}
                surface='cream'
                showRing={false}
                size='sm-responsive'
                scrollContainerRef={scrollContainerRef}
                className='tw-mx-2'
              />
            </Tabs>
            {/* Left fade gradient */}
            <div
              className='tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-3 tw-pointer-events-none'
              style={{ background: "linear-gradient(to right, var(--eggshell), transparent)" }}
            />
            {/* Right fade gradient */}
            <div
              className='tw-absolute tw-right-0 tw-top-0 tw-bottom-0 tw-w-3 tw-pointer-events-none'
              style={{ background: "linear-gradient(to left, var(--eggshell), transparent)" }}
            />
          </div>
          <div className='tw-flex-shrink-0 tw-pr-2'>
            <ScrollButton
              direction='right'
              onClick={scrollRight}
              visible={canScrollRight}
              ariaLabel={translations?.ui.scrollRight ?? "Scroll right"}
            />
          </div>
        </div>
      </div>

      {/* Table Content Area - Full Width */}
      <m.div
        className='tw-relative tw-min-h-[500px]'
        layout
        transition={{ layout: { duration: 0.15, ease: "easeInOut" } }}
      >
        <div className='tw-overflow-x-auto tw-scrollbar-none'>
          <LayoutGroup>
            <m.div
              key={selectedSection}
              layoutId='pricing-table-content'
              className='tw-min-w-fit'
              layout='position'
              transition={{ layout: { duration: 0.15, ease: "easeInOut" } }}
            >
              {/* Section Content */}
              {selectedSection === "productions" ? (
                <ProductionsSection />
              ) : (
                <SectionContent
                  section={selectedSection}
                  tiers={allTiers}
                  translations={translations}
                  mobileTier={selectedMobileTier}
                  onMobileTierChange={setSelectedMobileTier}
                  allTiers={allTiers}
                />
              )}
            </m.div>
          </LayoutGroup>
        </div>
      </m.div>
    </div>
  );
};

const defaultOffersId = (tier: Tier) => tier.planId !== "payg";

export const PricingTableTabs = ({
  className,
  offersId = defaultOffersId,
  models = DEFAULT_MODELS,
  products = DEFAULT_PRODUCTS,
  showStarterPromo,
  translations,
}: PricingTableTabsProps) => {
  const filterFn = typeof offersId === "function" ? offersId : (tier: Tier) => offersId.includes(tier.planId);
  const offers = getPricingTiers().filter(filterFn);

  if (showStarterPromo) {
    setStarterTierPromotion([...offers]);
  }

  return (
    offers.length > 0 && (
      <div className={className} id='pricing-table'>
        <PricingTableContent models={models} offersId={offersId} products={products} translations={translations} />
      </div>
    )
  );
};
